import IconPencil from "assets/img/icon-pencil.svg?react"

import { FC } from "react"
import { useTranslation } from "react-i18next"

import Summary from "components/Summary/Summary"
import useAssetFields, { AssetField } from "hooks/useAssetFields"
import ConnectedAsset from "models/ConnectedAsset"
import AssetDisplayStatus from "models/AssetDisplayStatus"
import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import CurrentAssetLocationMap from "components/CurrentAssetLocationMap/CurrentAssetLocationMap"
import { TenantWarehouse } from "models/TenantWarehouse"
import Button from "components/Button/Button"
import { AssetStep } from "pages/AssetPage/config"
import Routes from "@/routes"
import { useAssetDataContext } from "./AssetDataContext"
import Address from "models/Address"

interface IAssetDataLocationProps {
    connectedAssets?: ConnectedAsset[]
    displayStatus?: AssetDisplayStatus
    displayStatusLoading?: boolean
    homebaseWarehouse?: TenantWarehouse | null
    homebaseWarehouseDescription?: string | null
    eventAddress?: Address
}

const AssetDataLocation: FC<IAssetDataLocationProps> = ({
    connectedAssets,
    displayStatus,
    displayStatusLoading,
    homebaseWarehouse,
    homebaseWarehouseDescription,
    eventAddress,
}) => {
    const { t } = useTranslation()
    const { asset, article, pinnable, placement, getEditLink, onEditClick } = useAssetDataContext()
    const { getFields } = useAssetFields({
        asset,
        article,
        connectedAssets,
        displayStatus,
        displayStatusLoading,
        homebaseWarehouseDescription,
        eventAddress,
        pinnable,
    })

    if (placement === "summary") {
        return (
            <Summary data={[
                {
                    title: t("asset.location"),
                    items: getFields([
                        AssetField.coordinates,
                        AssetField.homebase,
                    ]),
                    editLink: getEditLink?.(AssetStep.location),
                    onEditClick: onEditClick ? () => onEditClick(AssetStep.location) : undefined,
                }
            ]} />
        )
    }

    return (
        <>
            <Flex className="mb-2" alignItems="center" justifyContent="space-between" gap="1.6rem">
                <Text type="h3">{t('asset.location')}</Text>
                {!!asset?.linkUpdate && !!asset.id &&
                    <Button
                        type="link"
                        url={Routes.assetEdit.getPath({ assetId: asset.id }, { backUrl: location.pathname + location.search, step: AssetStep.location })}
                        theme="transparent"
                        round
                        icon={<IconPencil />}
                        ariaLabel={t('common.edit')}
                    />
                }
            </Flex>
            <div className="overflow-hidden round-sm mb-2">
                <CurrentAssetLocationMap
                    height="220px"
                    homebaseWarehouse={homebaseWarehouse}
                    latitude={asset.assetLocation.location.lat}
                    longitude={asset.assetLocation.location.lng}
                    trackingAssetId={asset.id}
                />
            </div>
            <Summary data={[
                {
                    items: getFields([
                        AssetField.stateOfStorage,
                        AssetField.homebase,
                        AssetField.shelfStorage,
                        AssetField.shelfStorageDescription,
                    ]),
                }
            ]} />
        </>
    )
}

export default AssetDataLocation
